import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';

interface NumberInputProps {
  value?: number;
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  name?: string;
}

const clampValue = (value: number, min?: number, max?: number) => {
  if (typeof min === 'number' && value < min) return min;
  if (typeof max === 'number' && value > max) return max;
  return value;
};

const sanitizeValue = (value: string) => value.replace(/[^0-9.]/g, '');

export default function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  className,
  required,
  disabled,
  placeholder,
  id,
  name,
}: NumberInputProps) {
  const [inputValue, setInputValue] = useState(
    typeof value === 'number' ? String(value) : ''
  );

  useEffect(() => {
    setInputValue(typeof value === 'number' ? String(value) : '');
  }, [value]);

  const handleChange = (nextValue: string) => {
    const sanitized = sanitizeValue(nextValue);
    const decimalParts = sanitized.split('.');
    const normalized =
      decimalParts.length <= 2
        ? sanitized
        : `${decimalParts[0]}.${decimalParts.slice(1).join('')}`;

    setInputValue(normalized);

    if (!normalized) {
      onChange(undefined);
      return;
    }

    const numericValue = Number(normalized);
    if (!Number.isNaN(numericValue)) {
      onChange(clampValue(numericValue, min, max));
    }
  };

  const handleBlur = () => {
    if (!inputValue) return;

    const numericValue = Number(inputValue);
    if (Number.isNaN(numericValue)) {
      setInputValue('');
      onChange(undefined);
      return;
    }

    const clampedValue = clampValue(numericValue, min, max);
    setInputValue(String(clampedValue));
    onChange(clampedValue);
  };

  const handleStep = (direction: 1 | -1) => {
    const baseValue = inputValue ? Number(inputValue) : 0;
    const nextValue = clampValue(baseValue + direction * step, min, max);
    setInputValue(String(nextValue));
    onChange(nextValue);
  };

  return (
    <Form.Control
      type="text"
      inputMode="decimal"
      value={inputValue}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          handleStep(1);
        }

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          handleStep(-1);
        }
      }}
      className={className}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      id={id}
      name={name}
    />
  );
}
